import { createStore } from '@/common/pinia';
import type { ApiResponse } from '@/common/services/api.service-new';
import type {
  EsaCciChangeResponse,
  EsaCciClassGroup,
  EsaCciLayerResponse,
  EsaCciTimeseriesResponse,
} from './esa-cci.types';

class EsaCciStore {
  /**
   * `/esa-cci/layer` responses keyed by year (2000..2022). One response carries
   * BOTH scheme variants + BOTH legends, so the scheme toggle never needs a
   * second fetch for the same year. `<LandUseTool>` warms this on year change.
   */
  private layers: Map<number, ApiResponse<EsaCciLayerResponse>> = new Map();

  /** Per-AOI stats keyed by `${year}|${scheme}|${JSON.stringify(coords)}`. */
  private statsCache: Map<string, EsaCciClassGroup[]> = new Map();

  /** Per-AOI change responses keyed by `${from}|${to}|${scheme}|${JSON.stringify(coords)}`. */
  private changeCache: Map<string, EsaCciChangeResponse> = new Map();

  /** Per-AOI yearly timeseries keyed by `${scheme}|${JSON.stringify(coords)}` (year-independent — one call spans all years). */
  private timeseriesCache: Map<string, EsaCciTimeseriesResponse> = new Map();

  public getLayer(year: number): ApiResponse<EsaCciLayerResponse> | null {
    return this.layers.get(year) ?? null;
  }

  public setLayer(year: number, layer: ApiResponse<EsaCciLayerResponse>): void {
    this.layers.set(year, layer);
  }

  public getStatsCache(year: number, scheme: string, fingerprint: string): EsaCciClassGroup[] | undefined {
    return this.statsCache.get(EsaCciStore.statsKey(year, scheme, fingerprint));
  }

  public setStatsCache(year: number, scheme: string, fingerprint: string, groups: EsaCciClassGroup[]): void {
    EsaCciStore.setBounded(this.statsCache, EsaCciStore.statsKey(year, scheme, fingerprint), groups);
  }

  public getChangeCache(from: number, to: number, scheme: string, fingerprint: string): EsaCciChangeResponse | undefined {
    return this.changeCache.get(EsaCciStore.changeKey(from, to, scheme, fingerprint));
  }

  public setChangeCache(from: number, to: number, scheme: string, fingerprint: string, change: EsaCciChangeResponse): void {
    EsaCciStore.setBounded(this.changeCache, EsaCciStore.changeKey(from, to, scheme, fingerprint), change);
  }

  public getTimeseriesCache(scheme: string, fingerprint: string): EsaCciTimeseriesResponse | undefined {
    return this.timeseriesCache.get(EsaCciStore.timeseriesKey(scheme, fingerprint));
  }

  public setTimeseriesCache(scheme: string, fingerprint: string, ts: EsaCciTimeseriesResponse): void {
    EsaCciStore.setBounded(this.timeseriesCache, EsaCciStore.timeseriesKey(scheme, fingerprint), ts);
  }

  /**
   * Cap each AOI-keyed cache and evict the oldest entry once over capacity — the
   * keys embed the full stringified geometry, so without a bound a long session
   * browsing many zones/selections would retain every AOI's coords + response
   * indefinitely. Re-inserting on write keeps the eviction order most-recent-last.
   */
  private static readonly MAX_AOI_CACHE_ENTRIES = 64;

  private static setBounded<V>(cache: Map<string, V>, key: string, value: V): void {
    cache.delete(key);
    cache.set(key, value);
    if (cache.size > EsaCciStore.MAX_AOI_CACHE_ENTRIES) {
      const oldest = cache.keys().next().value;
      if (oldest !== undefined) {
        cache.delete(oldest);
      }
    }
  }

  private static statsKey(year: number, scheme: string, fingerprint: string): string {
    return `${year}|${scheme}|${fingerprint}`;
  }

  private static changeKey(from: number, to: number, scheme: string, fingerprint: string): string {
    return `${from}|${to}|${scheme}|${fingerprint}`;
  }

  private static timeseriesKey(scheme: string, fingerprint: string): string {
    return `${scheme}|${fingerprint}`;
  }
}

const useEsaCciStore = createStore(EsaCciStore);

export { useEsaCciStore, type EsaCciStore };
